import React, { useState } from 'react';
import ColorEditor from '../ColorEditor';

const CustomCodeEditor = ({ content, onContentChange, onMultipleContentChanges, businessInfo, site, onCreateCustomTheme }) => {
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showAiGenerator, setShowAiGenerator] = useState(false);
  const [generationError, setGenerationError] = useState('');
  const [includeExisting, setIncludeExisting] = useState(false);
  
  // Individual improvement states
  const [improveModal, setImproveModal] = useState({ show: false, type: '', prompt: '' });
  const [isImproving, setIsImproving] = useState(false);
  
  // Validation helper for required fields
  const isValidCode = (code) => {
    return code && code.trim().length > 0;
  };

  const hasAnyCode = () => {
    return isValidCode(content.htmlCode) || isValidCode(content.cssCode) || isValidCode(content.jsCode);
  };

  // Handle AI generation
  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) {
      setGenerationError('Please enter a description of what you want to create.');
      return;
    }

    setIsGenerating(true);
    setGenerationError('');

    try {
      const response = await fetch('/api/ai/generate-custom-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          prompt: aiPrompt,
          businessInfo: businessInfo
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate code');
      }

      const result = await response.json();
      console.log('✅ [AI-GENERATION] Received response with:', {
        success: result.success,
        htmlCode: result.htmlCode ? `${result.htmlCode.length} chars` : 'empty',
        cssCode: result.cssCode ? `${result.cssCode.length} chars` : 'empty', 
        jsCode: result.jsCode ? `${result.jsCode.length} chars` : 'empty'
      });
      
      if (result.success) {
        // Prepare all updates in a single object to avoid state batching issues
        const updates = {};
        
        if (result.htmlCode) {
          updates.htmlCode = result.htmlCode;
        }
        if (result.cssCode) {
          updates.cssCode = result.cssCode;
        }
        if (result.jsCode) {
          updates.jsCode = result.jsCode;
        }
        
        // Set title and description if provided
        if (result.title && !content.title) {
          updates.title = result.title;
        }
        if (result.description && !content.description) {
          updates.description = result.description;
        }
        
        // Apply all updates at once using the batched method
        console.log('🚀 [AI-GENERATION] Applying batched updates for:', Object.keys(updates).join(', '));
        onMultipleContentChanges(updates);
        
        // Close the AI generator
        setShowAiGenerator(false);
        setAiPrompt('');
      } else {
        setGenerationError(result.error || 'Failed to generate code');
      }
    } catch (error) {
      console.error('AI generation error:', error);
      setGenerationError('Something went wrong while generating code. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Clear all code fields
  const handleClearAll = () => {
    onMultipleContentChanges({
      htmlCode: '',
      cssCode: '',
      jsCode: ''
    });
  };

  // Handle individual code improvement
  const handleImproveCode = async (codeType, improvementPrompt) => {
    if (!improvementPrompt.trim()) {
      return;
    }

    const currentCode = content[`${codeType}Code`] || '';
    if (!currentCode.trim()) {
      alert('No existing code to improve. Please add some code first.');
      return;
    }

    setIsImproving(true);

    try {
      const response = await fetch('/api/ai/improve-custom-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          codeType,
          currentCode,
          improvementPrompt,
          businessInfo: businessInfo
        })
      });

      if (!response.ok) {
        throw new Error('Failed to improve code');
      }

      const result = await response.json();
      
      if (result.success) {
        // Update only the specific code field
        onContentChange(`${codeType}Code`, result.improvedCode);
        setImproveModal({ show: false, type: '', prompt: '' });
      } else {
        alert(result.error || 'Failed to improve code');
      }
    } catch (error) {
      console.error('Code improvement error:', error);
      alert('Something went wrong while improving code. Please try again.');
    } finally {
      setIsImproving(false);
    }
  };

  // Open improvement modal
  const openImproveModal = (codeType) => {
    setImproveModal({ show: true, type: codeType, prompt: '' });
  };

  return (
    <div className="space-y-6">
      {/* Section Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <div className="flex items-center">
          <div className="text-2xl mr-3">⚡</div>
          <div>
            <h3 className="text-sm font-medium text-blue-900">Custom Code Section</h3>
            <p className="text-xs text-blue-700 mt-1">
              Add custom HTML, CSS, and JavaScript code. Use with caution - ensure code is safe and tested.
            </p>
          </div>
        </div>
      </div>

      {/* Title and Description */}
      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Section Title (Optional)
          </label>
          <input
            type="text"
            value={content.title || ''}
            onChange={(e) => onContentChange('title', e.target.value)}
            placeholder="e.g., Analytics Tracking, Custom Widget..."
            className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description (Optional)
          </label>
          <textarea
            value={content.description || ''}
            onChange={(e) => onContentChange('description', e.target.value)}
            placeholder="Describe what this custom code does..."
            rows="4"
            className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* AI Generator Section */}
      <div className="border border-purple-200 rounded-lg p-2 bg-gradient-to-r from-purple-50 to-indigo-50">
        <div className="mb-1">
          <div className="flex items-center">
            <div>
              <h3 className="text-sm font-medium text-purple-900">AI Code Generator</h3>
              <p className="text-xs text-purple-700 mt-1 mb-2">
                Describe you want to create and AI will generate the HTML, CSS, and JavaScript
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowAiGenerator(!showAiGenerator)}
            className={`px-2 py-1 text-sm rounded-md transition-colors ${
              showAiGenerator 
                ? 'bg-purple-600 text-white' 
                : 'bg-white border border-purple-300 text-purple-700 hover:bg-purple-50'
            }`}
          >
            {showAiGenerator ? 'Cancel' : 'Generate'}
          </button>
        </div>

        {showAiGenerator && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-purple-900 mb-2">
                What do you want to create?
              </label>
              <textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder={`Examples:
• "Create an application form with name, surname, email, phone, job title, and address"
• "Create an HTML galaxy shooter game with moving stars and shooting mechanics"
• "Create a countdown timer for a product launch with animated numbers"
• "Create a contact form with validation and success message"
• "Create an interactive pricing calculator for services"

For improvements:
• "Add a high score save feature to the existing game"
• "Make the form more visually appealing with animations"
• "Add input validation and error messages"`}
                rows="4"
                className="w-full px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
              />
            </div>

            {/* Include existing content toggle */}
            {hasAnyCode() && (
              <div>
              <div className="flex items-center p-2 bg-purple-25 border border-purple-200 rounded-md">
                <input
                  type="checkbox"
                  id="includeExisting"
                  checked={includeExisting}
                  onChange={(e) => setIncludeExisting(e.target.checked)}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-purple-300 rounded"
                />
                <label htmlFor="includeExisting" className="ml-2 text-xs text-purple-900">
                  <strong>Improve existing content</strong> 
                </label>
                </div>
                <div className="ml-2 p-2 text-xs text-purple-700">
                  {includeExisting ? '✓ Current code will be enhanced' : '⚠️ Current code will be replaced'}
                </div>
              </div>
              
            )}

            {generationError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-800">{generationError}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleAiGenerate}
                disabled={isGenerating || !aiPrompt.trim()}
                className="flex-1 bg-purple-600 text-sm text-white px-2 py-2 rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isGenerating ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Generating...
                  </div>
                ) : (
                  '✨ Generate Code'
                )}
              </button>
              
              {hasAnyCode() && (
                <button
                  onClick={handleClearAll}
                  className="px-2 py-2 bg-gray-500 text-sm text-white rounded-md hover:bg-gray-600 transition-colors"
                >
                  Clear All
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Code Fields */}
      <div className="space-y-4">
        {/* HTML Code */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              HTML Code
            </label>
            {content.htmlCode && content.htmlCode.trim() && (
              <button
                onClick={() => openImproveModal('html')}
                className="text-xs px-2 py-1 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded transition-colors"
              >
                ✨ Improve
              </button>
            )}
          </div>
          <textarea
            value={content.htmlCode || ''}
            onChange={(e) => onContentChange('htmlCode', e.target.value)}
            placeholder={`<div class="my-custom-content">
  <h3>Custom HTML Content</h3>
  <p>Your custom HTML goes here...</p>
</div>`}
            rows="8"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs"
          />
        </div>

        {/* CSS Code */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              CSS Code
            </label>
            {content.cssCode && content.cssCode.trim() && (
              <button
                onClick={() => openImproveModal('css')}
                className="text-xs px-2 py-1 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded transition-colors"
              >
                ✨ Improve
              </button>
            )}
          </div>
          <textarea
            value={content.cssCode || ''}
            onChange={(e) => onContentChange('cssCode', e.target.value)}
            placeholder={`.my-custom-content {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
}

.my-custom-content h3 {
  color: #2563eb;
  margin-bottom: 10px;
}`}
            rows="8"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs"
          />
        </div>

        {/* JavaScript Code */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              JavaScript Code
            </label>
            {content.jsCode && content.jsCode.trim() && (
              <button
                onClick={() => openImproveModal('js')}
                className="text-xs px-2 py-1 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded transition-colors"
              >
                ✨ Improve
              </button>
            )}
          </div>
          <textarea
            value={content.jsCode || ''}
            onChange={(e) => onContentChange('jsCode', e.target.value)}
            placeholder={`// Your custom JavaScript code
console.log('Custom code loaded');

// Example: Add event listeners
document.addEventListener('DOMContentLoaded', function() {
  // Your code here
});`}
            rows="8"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs"
          />
        </div>
      </div>

      {/* Options */}
      <div className="space-y-4 border-t pt-6">
        <h4 className="text-sm font-medium text-gray-900">Display Options</h4>
        
        <div className="space-y-3">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isVisible"
              checked={content.isVisible !== false} // Default to true
              onChange={(e) => onContentChange('isVisible', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="isVisible" className="ml-2 text-xs text-gray-700">
              Section takes visual space (uncheck for invisible/zero-height sections)
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="enabled"
              checked={content.enabled !== false} // Default to true
              onChange={(e) => onContentChange('enabled', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="enabled" className="ml-2 text-xs text-gray-700">
              Enable this custom code section
            </label>
          </div>
        </div>

        {/* Advanced Options */}
        <div className="grid grid-cols-1">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CSS Scope
            </label>
            <select
              value={content.cssScope || 'global'}
              onChange={(e) => onContentChange('cssScope', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="global">Global (affects entire page)</option>
              <option value="scoped">Scoped (within style tag)</option>
              <option value="inline">Inline (with HTML)</option>
            </select>
          </div>
          <br />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              JavaScript Execution
            </label>
            <select
              value={content.jsExecution || 'immediate'}
              onChange={(e) => onContentChange('jsExecution', e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="immediate">Immediate</option>
              <option value="domready">On DOM Ready</option>
              <option value="windowload">On Window Load</option>
            </select>
          </div>
        </div>
      </div>

      {/* Warning/Info Messages */}
      {!hasAnyCode() && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <p className="text-sm text-yellow-800">
            ⚠️ Add at least one type of code (HTML, CSS, or JavaScript) to make this section functional.
          </p>
          <p className="text-xs text-yellow-700 mt-1">
            💡 Try using the AI Generator above to automatically create code from your description!
          </p>
        </div>
      )}

      {content.jsCode && content.jsCode.trim() && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
          <p className="text-sm text-amber-800">
            🔒 <strong>Security Notice:</strong> Only add trusted JavaScript code. Malicious code can harm your site and visitors.
          </p>
        </div>
      )}

      {!content.enabled && hasAnyCode() && (
        <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
          <p className="text-sm text-gray-600">
            ⏸️ This custom code section is disabled and won't execute on your live site.
          </p>
        </div>
      )}

      {/* Improvement Modal */}
      {improveModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              Improve {improveModal.type.toUpperCase()} Code
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                What improvements would you like?
              </label>
              <textarea
                value={improveModal.prompt}
                onChange={(e) => setImproveModal(prev => ({ ...prev, prompt: e.target.value }))}
                placeholder={`Examples for ${improveModal.type.toUpperCase()}:
${improveModal.type === 'css' ? '• Make colors darker\n• Add animations\n• Make it more responsive\n• Improve the button styling' : ''}
${improveModal.type === 'html' ? '• Add more content sections\n• Improve accessibility\n• Add form validation\n• Make it more semantic' : ''}
${improveModal.type === 'js' ? '• Add error handling\n• Improve performance\n• Add new features\n• Fix any bugs' : ''}`}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => handleImproveCode(improveModal.type, improveModal.prompt)}
                disabled={isImproving || !improveModal.prompt.trim()}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              >
                {isImproving ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Improving...
                  </div>
                ) : (
                  '✨ Improve Code'
                )}
              </button>
              
              <button
                onClick={() => setImproveModal({ show: false, type: '', prompt: '' })}
                disabled={isImproving}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Color Theme - Always at bottom */}
      <ColorEditor
        value={content.colorScheme || 'default'}
        onChange={(value) => onContentChange('colorScheme', value)}
        label="Section Color Theme"
        showReset={true}
        resetValue="default"
        siteTheme={site?.design?.colorTheme ? {
          name: site.design.colorThemeName || 'Site Theme',
          colors: site.design.colorTheme
        } : null}
        customThemes={site?.design?.customThemes || []}
        onCreateCustomTheme={onCreateCustomTheme}
      />
    </div>
  );
};

export default CustomCodeEditor;